import React, { useState, useEffect } from 'react';
import {
  Zap,
  Save,
  RotateCcw,
  Lock,
  Eye,
  Activity,
  RefreshCw,
  Radio,
  CheckCircle2,
  AlertCircle,
  Server,
  ShieldCheck,
  Trash2,
  Info,
  Sliders,
} from 'lucide-react';
import { SYSTEM_BILINGUAL } from '../shared/constants/bilingualTitles';

interface GatewayConfig {
  accountId: string;
  gatewayId: string;
  apiToken: string;
  model: string;
  customModel: string;
  providerApiKey: string;
}

interface TestResult {
  status: 'idle' | 'testing' | 'success' | 'error';
  httpCode?: number;
  rttMs?: number;
  responsePreview?: string;
  tokensUsed?: {
    prompt?: number;
    completion?: number;
    total?: number;
    neurons?: number;
  };
  errorMessage?: string;
  testedAt?: string;
}

const DEFAULT_CONFIG: GatewayConfig = {
  accountId: '74031127ffb3b4595fbdaf41971011c2',
  gatewayId: 'default',
  apiToken: 'cfut_c1zx34ShaHr3FYC3tRDSFkh642jUkuiGzUvfJO5E3def8d02',
  model: 'openai/gpt-5.6-luna',
  customModel: '',
  providerApiKey: '',
};

const POPULAR_MODELS = [
  { id: 'openai/gpt-5.6-luna', name: 'OpenAI GPT-5.6 Luna (Cloudflare 付费账号统一计费, 极速)', tag: '默认推荐 / 统一计费' },
  { id: '@cf/meta/llama-3.1-8b-instruct', name: 'Llama 3.1 8B Instruct (Workers AI 开箱即用, 极速)', tag: '推荐 / 免额外Key' },
  { id: '@cf/deepseek-ai/deepseek-r1-distill-qwen-32b', name: 'DeepSeek R1 Distill Qwen 32B (Workers AI 深度推理)', tag: '推荐 / 免额外Key' },
  { id: '@cf/qwen/qwen2.5-72b-instruct', name: 'Qwen 2.5 72B Instruct (Workers AI 高精度综合)', tag: '免额外Key' },
  { id: 'openai/gpt-4o', name: 'OpenAI GPT-4o (通过 AI Gateway 代理)', tag: '需 Provider Key / Unified Billing' },
  { id: 'custom', name: '自定义模型标识符...', tag: '高级' },
];

export const SettingsView: React.FC = () => {
  const [config, setConfig] = useState<GatewayConfig>(DEFAULT_CONFIG);
  const [showToken, setShowToken] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<TestResult>({ status: 'idle' });
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('market_hub_cf_gateway_config');
      if (saved) {
        const parsed = JSON.parse(saved);
        setConfig((prev) => ({ ...prev, ...parsed }));
      }
    } catch (e) {
      console.warn('Failed to load saved gateway config:', e);
    }
  }, []);

  const handleSave = () => {
    try {
      localStorage.setItem('market_hub_cf_gateway_config', JSON.stringify(config));
      setSaveStatus('配置已保存至本地存储 (LocalStorage)');
      setTimeout(() => setSaveStatus(null), 3500);
    } catch {
      setSaveStatus('保存失败，请检查浏览器权限');
    }
  };

  const handleReset = () => {
    if (window.confirm('确认重置为默认推荐网关配置？')) {
      setConfig(DEFAULT_CONFIG);
      localStorage.removeItem('market_hub_cf_gateway_config');
      setSaveStatus('已重置为系统默认推荐配置');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleRunTest = async () => {
    const selectedModel = config.model === 'custom' ? config.customModel.trim() : config.model;
    if (!selectedModel) {
      alert('请选择或输入有效的模型名称');
      return;
    }

    setTestResult({ status: 'testing' });
    const startTime = performance.now();

    try {
      const isWorkersAi = selectedModel.startsWith('@cf/');
      const isLuna = selectedModel === 'openai/gpt-5.6-luna';
      const basePath = `/cf-ai/v1/${config.accountId.trim()}/${config.gatewayId.trim()}`;
      
      let endpoint = '';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      let bodyData: Record<string, unknown> = {};

      if (isLuna) {
        // Cloudflare Workers AI Run endpoint for openai/gpt-5.6-luna
        endpoint = `/cf-api/client/v4/accounts/${config.accountId.trim()}/ai/run`;
        headers['Authorization'] = `Bearer ${config.apiToken.trim()}`;
        headers['cf-aig-authorization'] = `Bearer ${config.apiToken.trim()}`;
        if (config.gatewayId.trim()) {
          headers['cf-aig-gateway-id'] = config.gatewayId.trim();
        }
        bodyData = {
          model: 'openai/gpt-5.6-luna',
          input: {
            input: 'Summarize the benefits of quantitative AI rate limiting in three short bullets.',
            max_output_tokens: 256,
          },
        };
      } else if (isWorkersAi) {
        // Universal Gateway endpoint for Workers AI via reverse proxy
        endpoint = `${basePath}/workers-ai/${selectedModel}`;
        headers['Authorization'] = `Bearer ${config.apiToken.trim()}`;
        headers['cf-aig-authorization'] = `Bearer ${config.apiToken.trim()}`;
        bodyData = {
          messages: [
            { role: 'system', content: 'You are an AI assistant in quantitative finance. Be concise.' },
            { role: 'user', content: 'Ping! Please reply with: "Cloudflare AI Gateway Online" and current status in 1 sentence.' }
          ]
        };
      } else {
        // OpenAI format through AI Gateway Universal endpoint
        endpoint = `${basePath}/openai/chat/completions`;
        const authToken = config.providerApiKey.trim() || config.apiToken.trim();
        headers['Authorization'] = `Bearer ${authToken}`;
        headers['cf-aig-authorization'] = `Bearer ${config.apiToken.trim()}`;
        bodyData = {
          model: selectedModel.replace('openai/', ''),
          messages: [
            { role: 'user', content: 'Ping! Please reply with: "Cloudflare AI Gateway Online"' }
          ]
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });

      const elapsed = Math.round(performance.now() - startTime);
      const httpCode = response.status;
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        let content = '';
        if (data.result?.choices?.[0]?.message?.content) {
          content = data.result.choices[0].message.content;
        } else if (data.result?.response) {
          content = data.result.response;
        } else if (data.choices?.[0]?.message?.content) {
          content = data.choices[0].message.content;
        } else {
          content = JSON.stringify(data.result || data);
        }

        const usage = data.result?.usage || data.usage || {};
        setTestResult({
          status: 'success',
          httpCode,
          rttMs: elapsed,
          responsePreview: content,
          tokensUsed: {
            prompt: usage.prompt_tokens,
            completion: usage.completion_tokens,
            total: usage.total_tokens,
            neurons: usage.neurons,
          },
          testedAt: new Date().toLocaleTimeString(),
        });
      } else {
        let errMsg = data.errors?.[0]?.message || data.error?.message || response.statusText || '未知错误';
        if (httpCode === 401 && (errMsg.includes('Authentication error') || data.errors?.[0]?.code === 10000)) {
          errMsg = '【缺少 Workers AI 权限 (HTTP 401 / Code 10000)】实测检测到该 Token (5.6 Luna) 已成功关联 All accounts 与 AI Gateway，但调用模型推理 API 必须在 Permissions 中包含「Account -> Workers AI -> Read (或 Edit)」权限。解决方式：进入 Cloudflare 仪表盘 -> My Profile -> API Tokens -> 编辑此 Token -> 点击 Add more 增加「Account -> Workers AI -> Read/Edit」并保存即可！';
        } else if (httpCode === 402 || errMsg.includes('Insufficient balance')) {
          errMsg = '【网关预存余额不足 (HTTP 402)】网关认证检查已顺利通过！Cloudflare 已成功识别 openai/gpt-5.6-luna 模型，调用 Schema 完全有效。由于这是 OpenAI 商业大模型，Cloudflare 统一计费要求网关拥有预存余额。请进入 Cloudflare 控制台 (AI Gateway -> Manage Credits) 充值预存余额；或在下方填写 Provider API Key 使用自带 Key (BYOK) 模式。';
        } else if (httpCode === 403 && errMsg.includes('Gateway authentication is required')) {
          errMsg = '【网关未开启认证 (HTTP 403)】当前网关 c_f-ai 的 Authenticated Gateway 为关闭状态。请直接在上方点击「使用 default (已认证)」切换到已开启认证的网关，或在 Cloudflare 控制台将 c_f-ai 的「Authenticated Gateway」开关打开。';
        } else if (httpCode === 401 && errMsg.includes('Incorrect API key')) {
          errMsg += ' (提示：当前配置的是外部商业模型，需要提供有效的 Provider API Key)';
        }

        setTestResult({
          status: 'error',
          httpCode,
          rttMs: elapsed,
          errorMessage: errMsg,
          testedAt: new Date().toLocaleTimeString(),
        });
      }
    } catch (err: unknown) {
      const elapsed = Math.round(performance.now() - startTime);
      setTestResult({
        status: 'error',
        rttMs: elapsed,
        errorMessage: err instanceof Error ? err.message : String(err),
        testedAt: new Date().toLocaleTimeString(),
      });
    }
  };

  return (
    <div className="view-panel settings-view" id="view-settings" role="tabpanel" aria-labelledby="nav-settings">
      {/* 1. View Header (Unified, zero card box, matching Views 2, 3, 4) */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top settings-bilingual-header-top">
            <Sliders size={20} aria-hidden="true" color="var(--accent-cyan)" />
            <h2 className="view-main-title bilingual-header-title-zh" title="系统全局设置">
              {SYSTEM_BILINGUAL.settings.zh}
            </h2>
            <span className="badge badge-cyan settings-badge-config">CONFIG</span>
          </div>
          <span className="bilingual-header-subtitle-en">{SYSTEM_BILINGUAL.settings.en}</span>
          <p className="view-subtitle settings-view-subtitle">
            系统偏好、网络通讯参数与 Cloudflare AI Gateway 智能化运行时集成
          </p>
        </div>
        <div className="view-actions settings-header-actions">
          {saveStatus && <span className="save-toast">{saveStatus}</span>}
          <button className="btn-secondary settings-btn-reset" onClick={handleReset}>
            <RotateCcw size={13} aria-hidden="true" className="tab-icon" />
            <span>重置默认值</span>
          </button>
          <button className="btn-primary settings-btn-save" onClick={handleSave}>
            <Save size={13} aria-hidden="true" className="tab-icon" />
            <span>保存设置</span>
          </button>
        </div>
      </div>

      {/* 2. Cockpit Status Strip (Unified, matching Views 1, 2, 3) */}
      <section className="cockpit-status-bar settings-status-strip" id="settings-status-bar">
        <div className="status-strip-left settings-strip-left">
          <div className="strip-item settings-strip-item">
            <span className="settings-strip-label">AI 网关:</span>
            <strong style={{ color: testResult.status === 'success' ? 'var(--color-emerald)' : testResult.status === 'error' ? 'var(--color-rose)' : 'var(--accent-cyan)' }}>
              {testResult.status === 'success' ? `● 在线 (HTTP ${testResult.httpCode || 200})` : testResult.status === 'error' ? '✕ 握手异常' : testResult.status === 'testing' ? '◐ 测速中' : '○ 待测试'}
            </strong>
          </div>
          <div className="strip-item settings-strip-item">
            <span className="settings-strip-label">活动模型:</span>
            <span className="mono settings-strip-primary">
              {config.model === 'custom' ? (config.customModel || 'custom') : config.model.replace('openai/', '')}
            </span>
          </div>
          <div className="strip-item settings-strip-item">
            <span className="settings-strip-label">持久化引擎:</span>
            <span className="settings-strip-emerald">LocalStorage · 实时生效</span>
          </div>
          <div className="strip-item settings-strip-item">
            <span className="settings-strip-label">架构定位:</span>
            <span className="settings-strip-cyan">只读行情流 · 零阻塞依赖</span>
          </div>
        </div>
      </section>

      {/* Grid Layout for Settings Cards */}
      <div className="settings-grid">
        {/* Card 1: Cloudflare AI Gateway Configuration */}
        <div className="settings-card card-full">
          <div className="card-header">
            <div className="card-title-row">
              <span className="card-icon card-icon-cyan">
                <Zap size={18} aria-hidden="true" />
              </span>
              <div className="card-title bilingual-card-title">
                <span className="bilingual-card-title-zh">Cloudflare AI Gateway 配置与连通性测试</span>
                <span className="bilingual-card-subtitle-en">CLOUDFLARE AI GATEWAY CONFIG & CONNECTIVITY</span>
              </div>
            </div>
            <div className="gateway-status-pill">
              {testResult.status === 'success' && <span className="status-badge success">● 网关连通 (HTTP {testResult.httpCode})</span>}
              {testResult.status === 'error' && <span className="status-badge error">✕ 握手异常 ({testResult.httpCode || '网络故障'})</span>}
              {testResult.status === 'testing' && <span className="status-badge testing">◐ 正在发起测速...</span>}
              {testResult.status === 'idle' && <span className="status-badge idle">○ 待测试</span>}
            </div>
          </div>

          <div className="card-body">
            <p className="section-desc">
              Cloudflare AI Gateway 充当统一的高可用推理代理层，提供零延迟缓存、速率限制审计与跨大模型 Fallback。
              使用 <strong>Workers AI 原生模型</strong> 无需额外 API 密钥即可直接调用；调用外部商业模型时支持 BYOK (自带 Key)。
              <br />
              <span className="field-hint-row">
                <Info size={14} aria-hidden="true" className="icon-cyan" />
                <span>
                  <strong>架构定位说明</strong>：当前 Market-Hub 核心行情与指标域均为确定性纯只读数据流，零 LLM 依赖；本网关为前瞻性预留基础设施（用于未来对接 AI 量化分析/智能告警），目前不参与行情生产落盘与核心交易计算。
                </span>
              </span>
            </p>

            <div className="form-grid">
              {/* Account ID */}
              <div className="form-group">
                <label htmlFor="input-cf-account">
                  Cloudflare Account ID <span className="required-star">*</span>
                </label>
                <input
                  id="input-cf-account"
                  type="text"
                  className="settings-input mono"
                  value={config.accountId}
                  onChange={(e) => setConfig({ ...config, accountId: e.target.value })}
                  placeholder="例如: 74031127ffb3b4595fbdaf41971011c2"
                />
                <span className="form-hint">通过 <code>wrangler whoami</code> 或 Cloudflare 控制台仪表盘获取</span>
              </div>

              {/* Gateway ID */}
              <div className="form-group">
                <div className="field-header-row">
                  <label htmlFor="input-cf-gateway">
                    AI Gateway 名称 (Gateway ID) <span className="required-star">*</span>
                  </label>
                  <div className="field-action-group">
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setConfig({ ...config, gatewayId: 'default' })}
                      title="系统已开启认证的默认网关"
                    >
                      使用 default (已认证)
                    </button>
                    <span className="text-muted">|</span>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setConfig({ ...config, gatewayId: 'c_f-ai' })}
                    >
                      c_f-ai
                    </button>
                  </div>
                </div>
                <input
                  id="input-cf-gateway"
                  type="text"
                  className="settings-input mono"
                  value={config.gatewayId}
                  onChange={(e) => setConfig({ ...config, gatewayId: e.target.value })}
                  placeholder="例如: default 或 c_f-ai"
                />
                <span className="form-hint">支持已开启身份验证的 <code>default</code> 网关或自定义网关 <code>c_f-ai</code></span>
              </div>

              {/* API Token */}
              <div className="form-group col-span-2">
                <div className="field-header-row">
                  <label htmlFor="input-cf-token">
                    Cloudflare API Token / OAuth Token <span className="required-star">*</span>
                  </label>
                  <button
                    type="button"
                    className="btn-link btn-link-inline"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? (
                      <>
                        <Lock size={13} aria-hidden="true" />
                        <span>隐藏凭证</span>
                      </>
                    ) : (
                      <>
                        <Eye size={13} aria-hidden="true" />
                        <span>显示明文</span>
                      </>
                    )}
                  </button>
                </div>
                <input
                  id="input-cf-token"
                  type={showToken ? 'text' : 'password'}
                  className="settings-input mono"
                  value={config.apiToken}
                  onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                  placeholder="Bearer Token (cfoat_... 或用户专属 API 令牌)"
                />
                <span className="form-hint">
                  具备 Workers AI 与 AI Gateway 调用权限的凭证，仅保存在用户本地浏览器 LocalStorage 中
                </span>
              </div>

              {/* Model Selector */}
              <div className="form-group col-span-2">
                <label htmlFor="select-cf-model">推理模型引擎 (Model Engine)</label>
                <select
                  id="select-cf-model"
                  className="settings-select"
                  value={config.model}
                  onChange={(e) => setConfig({ ...config, model: e.target.value })}
                >
                  {POPULAR_MODELS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} [{m.tag}]
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Model Input */}
              {config.model === 'custom' && (
                <div className="form-group col-span-2">
                  <label htmlFor="input-custom-model">自定义模型端点/ID</label>
                  <input
                    id="input-custom-model"
                    type="text"
                    className="settings-input mono"
                    value={config.customModel}
                    onChange={(e) => setConfig({ ...config, customModel: e.target.value })}
                    placeholder="例如: @cf/meta/llama-3.2-3b-instruct 或 anthropic/claude-3-5-sonnet"
                  />
                </div>
              )}

              {/* External Provider API Key (if using OpenAI / non-Workers AI) */}
              {!config.model.startsWith('@cf/') && config.model !== 'custom' && (
                <div className="form-group col-span-2 provider-key-box">
                  <label htmlFor="input-provider-key">
                    Provider API Key (可选 / BYOK 模式)
                  </label>
                  <input
                    id="input-provider-key"
                    type="password"
                    className="settings-input mono"
                    value={config.providerApiKey}
                    onChange={(e) => setConfig({ ...config, providerApiKey: e.target.value })}
                    placeholder="sk-proj-... (若 Gateway 未开启 Unified Billing，则需填写对应厂商 API Key)"
                  />
                  <span className="form-hint">
                    代理 OpenAI 或 Anthropic 时，可通过 BYOK 自带 Key 经由 Cloudflare AI Gateway 统一加速与统计
                  </span>
                </div>
              )}
            </div>

            {/* Test Action Row */}
            <div className="test-action-bar">
              <button
                id="btn-test-gateway"
                className="btn-test-gateway btn-test-gateway-flex"
                onClick={handleRunTest}
                disabled={testResult.status === 'testing'}
              >
                {testResult.status === 'testing' ? (
                  <>
                    <RefreshCw size={14} className="spin" aria-hidden="true" />
                    <span>正在测速与诊断中...</span>
                  </>
                ) : (
                  <>
                    <Activity size={14} aria-hidden="true" />
                    <span>发起 AI Gateway 连通性测试</span>
                  </>
                )}
              </button>
              <div className="test-endpoint-hint mono">
                端点: <code>{config.model === 'openai/gpt-5.6-luna' ? `/cf-api/client/v4/accounts/${config.accountId || '{account}'}/ai/run (统一计费网关)` : `/cf-ai/v1/${config.accountId || '{account}'}/${config.gatewayId || '{gateway}'}/...`}</code>
              </div>
            </div>

            {/* Diagnostic Results Box */}
            {testResult.status !== 'idle' && (
              <div
                className={`diagnostic-box ${testResult.status === 'success' ? 'diag-success' : ''} ${testResult.status === 'error' ? 'diag-error' : ''} ${testResult.status === 'testing' ? 'diag-testing' : ''}`}
              >
                <div className="diag-header">
                  <div className="diag-title diag-title-flex">
                    {testResult.status === 'testing' && (
                      <>
                        <Radio size={14} aria-hidden="true" className="spin" />
                        <span>正在向 Cloudflare AI Gateway 发起真实握手...</span>
                      </>
                    )}
                    {testResult.status === 'success' && (
                      <>
                        <CheckCircle2 size={14} aria-hidden="true" className="icon-emerald" />
                        <span>AI Gateway 连通测试通过 (HTTP 200 OK)</span>
                      </>
                    )}
                    {testResult.status === 'error' && (
                      <>
                        <AlertCircle size={14} aria-hidden="true" className="icon-bearish" />
                        <span>AI Gateway 握手异常</span>
                      </>
                    )}
                  </div>
                  {testResult.rttMs !== undefined && (
                    <div className="diag-meta mono">
                      往返延迟: <strong style={{ color: testResult.rttMs < 600 ? 'var(--color-emerald)' : 'var(--color-warning)' }}>{testResult.rttMs} ms</strong>
                      {testResult.testedAt && <span> · 时间: {testResult.testedAt}</span>}
                    </div>
                  )}
                </div>

                {testResult.status === 'success' && (
                  <div className="diag-body">
                    <div className="diag-field">
                      <span className="diag-label">模型响应内容:</span>
                      <div className="diag-response mono">{testResult.responsePreview}</div>
                    </div>

                    {testResult.tokensUsed && (
                      <div className="diag-stats-row mono">
                        <span>输入 Tokens: <strong>{testResult.tokensUsed.prompt ?? '--'}</strong></span>
                        <span>输出 Tokens: <strong>{testResult.tokensUsed.completion ?? '--'}</strong></span>
                        <span>总 Tokens: <strong>{testResult.tokensUsed.total ?? '--'}</strong></span>
                        {testResult.tokensUsed.neurons !== undefined && (
                          <span>神经元负载 (Neurons): <strong>{Number(testResult.tokensUsed.neurons).toFixed(4)}</strong></span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {testResult.status === 'error' && (
                  <div className="diag-body">
                    <div className="diag-error-box mono">
                      <strong>错误原因:</strong> {testResult.errorMessage}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Card 2: System Environment & Runtime Info */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-title-row">
              <span className="card-icon card-icon-cyan">
                <Server size={18} aria-hidden="true" />
              </span>
              <div className="card-title bilingual-card-title">
                <span className="bilingual-card-title-zh">系统节点与架构信息</span>
                <span className="bilingual-card-subtitle-en">SYSTEM NODE & ARCHITECTURE SPEC</span>
              </div>
            </div>
          </div>
          <div className="card-body">
            <table className="settings-info-table mono">
              <tbody>
                <tr>
                  <td>部署环境:</td>
                  <td>Oracle Cloud VPS (Ubuntu 26.04 LTS ARM64 Ampere A1 · NRT)</td>
                </tr>
                <tr>
                  <td>Web 服务器:</td>
                  <td>Caddy v2.8 (自动 HTTPS / 静态资源 SSOT / BFF 动态反代)</td>
                </tr>
                <tr>
                  <td>数据时序后端:</td>
                  <td>Dual TimescaleDB (加密 :5432 物理隔离 + 多资产 :5433 活跃)</td>
                </tr>
                <tr>
                  <td>前端治理规范:</td>
                  <td>ASOP v1.9.6 / Pure Decimal AST Precision</td>
                </tr>
                <tr>
                  <td>系统快捷键:</td>
                  <td><code>1-8, 0</code> 视图切换 · <code>[</code> 侧栏折叠 · <code>C</code> Copilot 中枢</td>
                </tr>
                <tr>
                  <td>全息系统接地:</td>
                  <td>View 7 全息系统接地 (离线 SSOT + 动态 600s TTL 遥测快照)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Card 3: Storage & Cache Preference */}
        <div className="settings-card">
          <div className="card-header">
            <div className="card-title-row">
              <span className="card-icon card-icon-emerald">
                <ShieldCheck size={18} aria-hidden="true" />
              </span>
              <div className="card-title bilingual-card-title">
                <span className="bilingual-card-title-zh">本地缓存与客户端安全</span>
                <span className="bilingual-card-subtitle-en">LOCAL STORAGE & CLIENT SECURITY</span>
              </div>
            </div>
          </div>
          <div className="card-body">
            <p className="section-desc">
              遵循零敏感信息泄露设计原则，所有 API Token 及网关偏好均仅保存在您本地浏览器的 LocalStorage 中，
              不上传任何第三方服务器，确保私钥与生产凭证的安全。
            </p>
            <div className="settings-danger-box">
              <button
                className="btn-secondary btn-flex"
                onClick={() => {
                  if (window.confirm('确定清除本地已缓存的指标及会话数据？')) {
                    localStorage.clear();
                    alert('本地缓存已完全清除');
                    window.location.reload();
                  }
                }}
              >
                <Trash2 size={14} aria-hidden="true" />
                <span>清除本地缓存与会话</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
